import React from 'react';
import { Flex, UnderlineTabs, UnderlineTab } from '@chatsdet/ui';
import { ChatCircle, Pulse, Robot, SlidersHorizontal } from '@phosphor-icons/react';
import type { WorkflowNode } from '../../../../types/workflow';
import type { NodeConfigPanelHandle } from '../../../components/NodeConfigPanel';
import type { ActionPageContext, FlowView, SidebarTab } from '../types';
import type { NodeValidationError } from '../validation/types';
import type {
  WorkflowInputDefinition,
  WorkflowManagementStatus,
  WorkflowScenario,
} from '@chatsdet/types';
import { ConfigureTabPanel } from './ConfigureTabPanel';
import { MultiSelectSummary } from '../MultiSelectSummary';
import { RunsTabPanel } from '../runs/RunsTabPanel';
import { ApiSequenceRunsPanel } from '../api';
import { useApiSequence, useApiSequenceExecute } from '../api/hooks';
import { LegacyChatTombstone } from '../../../../features/assistant/LegacyChatTombstone';
import { FlowCommentsPanel } from './FlowCommentsPanel';
import { useWorkflowComments } from '../hooks/useWorkflowComments';
import type { TicketInput } from '../hooks/useWorkflowEvents';
import {
  FLOW_DESIGNER_SIDEBAR_DEFAULT_WIDTH,
  FLOW_DESIGNER_SIDEBAR_MAX_WIDTH,
  FLOW_DESIGNER_SIDEBAR_MIN_WIDTH,
} from '../hooks/useResizableSidebarWidth';

type SidebarResizeHandleProps = {
  onPointerDown: React.PointerEventHandler<HTMLDivElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
};
type FlowDesignerSidebarProps = {
  activeSidebarTab: SidebarTab;
  selectSidebarTab: (tab: SidebarTab) => void;
  readOnly?: boolean;
  // Configure tab
  selectedNodeIds: Set<string>;
  nodes: WorkflowNode[];
  enrichedNodes: WorkflowNode[];
  selectedNodeForConfig: WorkflowNode | null;
  selectedNodePageContext: ActionPageContext | null;
  selectedNodePropertyId: string | null;
  upstreamSeedWorkflowId: string | null;
  workflowId: string | null;
  workflowPropertyId: string | null;
  /**
   * The workflow's `environmentsPropertyId` — used for the Runs tab's "Link
   * issue" file-new-issue mode (see `RunsTabPanel.propertyId`). Distinct from
   * `workflowPropertyId` (derived from a login credential, can be out of
   * scope for the workflow's own property), matching what `RunDetailPage`
   * uses for the same purpose.
   */
  runsPropertyId: string | null;
  nodeConfigPanelRef: React.RefObject<NodeConfigPanelHandle | null>;
  onUpdateNode: (nodeId: string, data: Record<string, any>) => Promise<void> | void;
  handleCloseConfigPanel: () => void;
  onDeselectNode: () => void;
  handleMultiNodeDelete: (nodeIds: string[]) => Promise<void>;
  onCopyNodes?: () => void;
  flowValidation: {
    getNodeErrors: (nodeId: string) => NodeValidationError[] | undefined;
  };
  // Runs tab
  live: {
    isRunning: boolean;
    isReconnecting: boolean;
    runId: string | null;
    events: any[];
    currentStepId: string | null;
    screenshotItems: any[];
    reconnectToRun: (runId: string) => void;
  };
  builder: {
    isGenerating: boolean;
    streamId: string | null;
    nodeCount: number;
    onCancel: () => void;
  };
  setHistoricalFailedNodeId: (nodeId: string | null) => void;
  onNodeSelect: (nodeId: string) => void;
  // API view
  flowView: FlowView;
  // Cache debug overlay
  onOpenCacheDebug?: (runId: string) => void;
  // Workflow inputs
  workflowInputs?: WorkflowInputDefinition[];
  workflowFlowType?: string;
  onSaveInputs?: (inputs: WorkflowInputDefinition[]) => void;
  isSavingInputs?: boolean;
  // Workflow scenarios
  workflowScenarios?: WorkflowScenario[];
  onSaveScenarios?: (scenarios: WorkflowScenario[]) => void;
  isSavingScenarios?: boolean;
  onOpenScenarioConfig?: () => void;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  isResizing?: boolean;
  resizeHandleProps?: SidebarResizeHandleProps;
  // Comments tab — clicking a node-reference chip scrolls the canvas to it
  onCommentNodeClick?: (nodeId: string) => void;
  /** Manual lifecycle axis, distinct from approval/health. Omitted while the workflow hasn't loaded. */
  workflowManagementStatus?: WorkflowManagementStatus;
  onChangeManagementStatus?: (input: {
    status: WorkflowManagementStatus;
    reason?: string;
    tickets?: TicketInput[];
  }) => void;
  isChangingManagementStatus?: boolean;
};

export const FlowDesignerSidebar: React.FC<FlowDesignerSidebarProps> = ({
  activeSidebarTab,
  selectSidebarTab,
  readOnly,
  selectedNodeIds,
  nodes,
  enrichedNodes,
  selectedNodeForConfig,
  selectedNodePageContext,
  selectedNodePropertyId,
  upstreamSeedWorkflowId,
  workflowId,
  workflowPropertyId,
  runsPropertyId,
  nodeConfigPanelRef,
  onUpdateNode,
  handleCloseConfigPanel,
  onDeselectNode,
  handleMultiNodeDelete,
  onCopyNodes,
  flowValidation,
  live,
  builder,
  setHistoricalFailedNodeId,
  onNodeSelect,
  flowView,
  onOpenCacheDebug,
  workflowInputs,
  workflowFlowType,
  onSaveInputs,
  isSavingInputs,
  workflowScenarios,
  onSaveScenarios,
  isSavingScenarios,
  onOpenScenarioConfig,
  width = FLOW_DESIGNER_SIDEBAR_DEFAULT_WIDTH,
  minWidth = FLOW_DESIGNER_SIDEBAR_MIN_WIDTH,
  maxWidth = FLOW_DESIGNER_SIDEBAR_MAX_WIDTH,
  isResizing = false,
  resizeHandleProps,
  onCommentNodeClick,
  workflowManagementStatus,
  onChangeManagementStatus,
  isChangingManagementStatus,
}) => {
  // Use the enriched version of the selected node for the config panel header
  // so derived labels (e.g. credentialName for seed credentials) are displayed.
  const enrichedSelectedNode = React.useMemo(() => {
    if (!selectedNodeForConfig) return null;
    return enrichedNodes.find((n) => n.id === selectedNodeForConfig.id) ?? selectedNodeForConfig;
  }, [selectedNodeForConfig, enrichedNodes]);

  // The dedicated API view owns its editing surface, so the node-config panel
  // stays out of the way and the Configure tab shows flow-level sections.
  const hideNodeConfigPanel = flowView === 'api';

  const apiSequenceQ = useApiSequence(flowView === 'api' ? workflowId : null);
  const apiSequenceExecute = useApiSequenceExecute(apiSequenceQ.data?.id ?? null, workflowId);
  const [lastExecuteResult, setLastExecuteResult] = React.useState<{
    apiSequenceId: string;
    runId: string;
    result: import('@chatsdet/types').ApiSequenceResult;
  } | null>(null);

  const handleApiSequenceExecute = React.useCallback(() => {
    const apiSequenceId = apiSequenceQ.data?.id;
    apiSequenceExecute.mutate(undefined, {
      onSuccess: (data) => {
        // Stamp the owning sequence id so a stale result from a previous flow
        // (this sidebar isn't remounted on flow-to-flow navigation) can't be
        // mistaken for the currently viewed sequence's result.
        if (!apiSequenceId) return;
        setLastExecuteResult({ apiSequenceId, ...data.data });
      },
    });
  }, [apiSequenceExecute, apiSequenceQ.data?.id]);

  // Drives the count badge on the Comments tab; shares the query cache with the
  // panel (same key), so opening the tab doesn't refetch.
  const commentsQ = useWorkflowComments(workflowId);
  const commentCount = commentsQ.data?.length ?? 0;

  return (
  <div
    role="complementary"
    aria-label="Flow details"
    className={`relative shrink-0 overflow-hidden border-l border-border bg-card flex flex-col h-full min-h-0 ${isResizing ? 'select-none' : ''}`}
    style={{ width, minWidth, maxWidth }}
  >
    {resizeHandleProps ? (
      <div
        {...resizeHandleProps}
        role="separator"
        aria-label="Resize flow details panel"
        aria-orientation="vertical"
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        aria-valuenow={width}
        tabIndex={0}
        title="Resize flow details panel"
        className="group absolute -left-1.5 top-0 z-20 h-full w-3 cursor-col-resize touch-none outline-none"
      >
        <div className="mx-auto h-full w-px bg-transparent transition-colors group-hover:bg-primary/50 group-focus-visible:bg-primary group-active:bg-primary/70" />
      </div>
    ) : null}
    <div className="flex flex-col h-full">
      <UnderlineTabs
        selectedId={activeSidebarTab}
        onSelectionChange={(id) => selectSidebarTab(id as SidebarTab)}
        layoutId="flow-designer-sidebar-tabs"
        className="pt-[16.5px]"
      >
        <UnderlineTab id="config" className="flex-1 py-3">
          <Flex align="center" gap="xs" justify="center">
            <SlidersHorizontal size={16} weight="bold" />
            Configure
          </Flex>
        </UnderlineTab>
        <UnderlineTab id="run" className="flex-1 py-3">
          <Flex align="center" gap="xs" justify="center">
            <Pulse size={16} weight="bold" />
            Runs
          </Flex>
        </UnderlineTab>
        <UnderlineTab id="ai" className="flex-1 py-3">
          <Flex align="center" gap="xs" justify="center">
            <Robot size={16} weight="bold" />
            Chat
          </Flex>
        </UnderlineTab>
        <UnderlineTab id="comments" className="flex-1 py-3">
          <Flex align="center" gap="xs" justify="center">
            <span className="relative inline-flex">
              <ChatCircle size={16} weight="bold" />
              {commentCount > 0 && (
                <span className="absolute -right-2 -top-1.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-none text-primary-foreground">
                  {commentCount > 99 ? '99+' : commentCount}
                </span>
              )}
            </span>
            Activity
          </Flex>
        </UnderlineTab>
      </UnderlineTabs>

      {activeSidebarTab === 'config' && (
        <div className="flex-1 overflow-hidden flex flex-col">
          {selectedNodeIds.size > 1 ? (
            <MultiSelectSummary
              selectedNodeIds={selectedNodeIds}
              nodes={nodes}
              onDeleteSelected={() => handleMultiNodeDelete([...selectedNodeIds])}
              onCopySelected={onCopyNodes}
            />
          ) : (
            <ConfigureTabPanel
              isActive={true}
              selectedNode={hideNodeConfigPanel ? null : enrichedSelectedNode}
              inferredPageContext={selectedNodePageContext}
              inferredPropertyId={selectedNodePropertyId}
              upstreamSeedWorkflowId={upstreamSeedWorkflowId}
              workflowId={workflowId}
              nodeConfigPanelRef={nodeConfigPanelRef}
              onUpdateNode={onUpdateNode}
              onDismiss={handleCloseConfigPanel}
              onDeselectNode={onDeselectNode}
              onNodeSelect={onNodeSelect}
              validationErrors={selectedNodeForConfig ? flowValidation.getNodeErrors(selectedNodeForConfig.id) : undefined}
              onCopyNode={onCopyNodes}
              readOnly={readOnly}
              workflowInputs={workflowInputs}
              workflowFlowType={workflowFlowType}
              onSaveInputs={onSaveInputs}
              isSavingInputs={isSavingInputs}
              workflowScenarios={workflowScenarios}
              onSaveScenarios={onSaveScenarios}
              isSavingScenarios={isSavingScenarios}
              onOpenScenarioConfig={onOpenScenarioConfig}
              nodeCount={nodes.length}
              onSwitchToRunsTab={() => selectSidebarTab('run')}
            />
          )}
        </div>
      )}

      {/* No horizontal gutter here — the run history is a full-bleed list, so
          its row separators and hover states run edge to edge and each row
          supplies its own `px-4`. `ApiSequenceRunsPanel` still renders as
          inset content and keeps the gutter. */}
      {activeSidebarTab === 'run' && (
        <div className={`flex-1 overflow-hidden ${flowView === 'api' ? 'px-4' : ''}`}>
          {flowView === 'api' ? (
            <ApiSequenceRunsPanel
              apiSequenceId={apiSequenceQ.data?.id ?? null}
              apiSequenceData={apiSequenceQ.data ?? null}
              workflowId={workflowId}
              workflowPropertyId={workflowPropertyId}
              isExecuting={apiSequenceExecute.isPending}
              onExecute={handleApiSequenceExecute}
              lastExecuteResult={lastExecuteResult}
            />
          ) : (
            <RunsTabPanel
              workflowId={workflowId}
              nodes={enrichedNodes}
              isActive={true}
              live={live}
              builder={builder}
              onFailedNodeChange={setHistoricalFailedNodeId}
              onOpenCacheDebug={onOpenCacheDebug}
              propertyId={runsPropertyId}
            />
          )}
        </div>
      )}

      {activeSidebarTab === 'ai' && (
        <div className="flex-1 overflow-hidden flex flex-col">
          <LegacyChatTombstone
            className="h-full"
            title="Flow Designer chat has moved"
            description="Use the page-aware Assistant to plan and update this workflow from anywhere."
          />
        </div>
      )}

      {activeSidebarTab === 'comments' && (
        <div className="flex-1 overflow-y-auto">
          {workflowId ? (
            <FlowCommentsPanel
              workflowId={workflowId}
              onNodeClick={onCommentNodeClick}
              workflowManagementStatus={workflowManagementStatus}
              onChangeManagementStatus={onChangeManagementStatus}
              isChangingManagementStatus={isChangingManagementStatus}
            />
          ) : null}
        </div>
      )}
    </div>
  </div>
  );
};
